import React, { FormEvent, useRef, useState } from 'react';
import { Container } from '@/components/Containers/Container';
import { Typography } from '@/components/Typography/Typography';
import { clsx } from 'clsx';
import style from '@/components/PagesSections/FreshdeskIntegration/SuperChargeWithAgent/SuperChargeWithAgent.module.css';
import { NewRequestDemoForm } from '@/components/NewRequestDemoForm/NewRequestDemoForm';
import { BlackButton } from '@/components/ButtonsCollection/BlackButton/BlackButton';
import { COLORS } from '@/constants/colors';
import { SmallArrowIcon } from '@/assets/icons/smallArrowIcon';
import { PATH_SUCCESS } from '@/constants/spa-routes';
import { useRouter } from 'next/router';
import Image from 'next/image';

type TransparentPricingProps = {
  isMobile: boolean;
};

const wait = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const TransparentPricing = ({ isMobile }: TransparentPricingProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<React.RefObject<HTMLDivElement> | undefined>();
  const { push } = useRouter();
  const onButtonClick = async (event: FormEvent) => {
    if (!isExpanded) {
      event.preventDefault();
      setIsExpanded(true);
      //@ts-ignore
      containerRef.current.style.height =
        //@ts-ignore
        containerRef.current.firstChild.clientHeight + 'px';

      await wait(1500);
      //@ts-ignore
      containerRef.current.style.overflow = 'visible';
    }
  };

  const onFormSubmit = async (response: any) => {
    if (response.success) {
      setIsSuccess(true);
      //@ts-ignore
      window.scrollTo(0, containerRef.current.offsetParent.offsetTop);
      await wait(300);
      setIsExpanded(false);
      //@ts-ignore
      containerRef.current.style.overflow = 'hidden';
      //@ts-ignore
      containerRef.current.style.height = 0;

      const name = response.data?.name;
      const email = response.data?.email;
      const url = `${PATH_SUCCESS.ROOT}?name=${encodeURI(name || '')}&email=${encodeURI(email || '')}`;

      await push(url);
    }
  };

  return (
    <section className={clsx('relative bg-white')}>
      <Container className="md:py-32-vw py-15">
        <div className="z-10 flex max-lg:flex-col lg:gap-6-vw gap-6">
          <div className="flex flex-col lg:w-1/2 justify-center items-start md:gap-7.5-vw gap-7.5 md:my-5-vw my-5">
            <div className="w-full flex-col flex justify-center items-start lg:gap-2.5-vw gap-2.5 md:max-w-[59.444vw]">
              <Typography
                $type="Heading1"
                as="h2"
                className="font-formula md:max-w-[47.625vw] text-start"
                $weight="700"
              >
                Transparent Pricing Policy
              </Typography>
              <Typography $type="Heading4">
                Most AI solutions for customer support are opaque and come with many hidden costs.
                CoSupport AI for Freshdesk offers straightforward and transparent investments:
              </Typography>
            </div>
            <div className="flex justify-center items-center max-lg:flex-col lg:gap-6-vw gap-6 relative w-full">
              <div>
                <div className={style.title}>
                  {
                    <p className={`${style.successMessage} ${isSuccess ? style.visible : ''}`}>
                      <span className={'green'}>Great, you're set!</span>
                      <br />
                      Thanks for opting to explore our demo – look out for our follow-up. Please
                      check your email! <span className={'green'}>→</span>
                    </p>
                  }
                </div>
                <div
                  //@ts-ignore
                  ref={containerRef}
                  style={{ height: 0, width: '90%' }}
                  className={`${style.formContainer} ${isSuccess ? style.hidden : ''}`}
                >
                  <div>
                    <div>
                      <Typography className="font-jamjuree italic" $weight="700" $type="PMedium">
                        For a tailored experience, please fill out this form. It'll help us
                        customize your demo preview.
                      </Typography>
                    </div>
                    <NewRequestDemoForm
                      tags={['Freshdesk Page - Pricing']}
                      id="transparentPricingPolicyForm"
                      name={'required'}
                      email={{
                        required: true,
                        validationPopupPosition: isMobile ? 'bottom' : 'right',
                      }}
                      phone
                      tickets={{ required: true }}
                      message
                      darkMode={false}
                      onSubmit={onFormSubmit}
                      showSpinner={true}
                    />
                  </div>
                </div>
                {!isSuccess && (
                  <BlackButton
                    onClick={onButtonClick}
                    form={'transparentPricingPolicyForm'}
                    className="whitespace-nowrap"
                    linkBtnClassName="max-md:w-full"
                  >
                    <Typography as="span" $color={COLORS.WHITE} $type="Heading4" $weight="700">
                      Request a Quote
                    </Typography>
                    <SmallArrowIcon />
                  </BlackButton>
                )}
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 flex items-start justify-center">
            <div className="flex max-lg:flex-col gap-6 justify-center items-center lg:gap-6-vw border rounded-2xl w-full lg:py-24-vw py-11 px-12.5">
              <Image
                width="588"
                height="300"
                src="/img/freshdesk-integration/transparent-pricing.webp"
                className="max-md:min-w-[120px] max-w-[196px] max-h-[103px] w-auto"
                alt="transparent-pricing-icon"
              />
              <Typography
                $type="Heading3"
                className="font-formula lg:max-w-[14.028vw] max-lg:text-center"
              >
                <span className="lg:block hidden">Pay based on the number of AI responses</span>
                <span className="max-lg:block hidden">
                  Pay based on the{' '}
                  <b>
                    <i>number of interactions</i>
                  </b>{' '}
                  between the client and the AI model
                </span>
              </Typography>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
